import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react';
import { ethers } from 'ethers';
import { useWallet } from './WalletContext';
import { useNotification } from './NotificationContext';
import { ACTIVE_CONTRACTS, LOGIC_ABI, VIEWS_ABI, STORAGE_ABI, ERC20_ABI } from '../config/contract';
import { readProvider } from '../config/provider';
import { DEFAULT_REFERRER_ID } from '../config/packages';
import { parseContractError } from '../utils/contractErrors';

const ContractContext = createContext(null);

export const useContract = () => {
  const context = useContext(ContractContext);
  if (!context) {
    throw new Error('useContract must be used within ContractProvider');
  }
  return context;
};

export const ContractProvider = ({ children }) => {
  const { provider, signer, account, isConnected, isCorrectChain } = useWallet();
  const { success, error: showError } = useNotification();
  
  const [userId, setUserId] = useState(null);
  const [isRegistered, setIsRegistered] = useState(false);
  const [isCheckingRegistration, setIsCheckingRegistration] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tokenBalance, setTokenBalance] = useState('0');
  const [tokenAllowance, setTokenAllowance] = useState('0');
  const [isOwner, setIsOwner] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false); // true for owner OR admin

  // Shared fast read-only provider (all reads go through it, never the wallet's RPC)
  const readOnlyProvider = readProvider;

  // Short-lived read cache: page switches reuse data instead of hitting the RPC again.
  // Stores the promise itself so parallel callers share one request.
  const cacheRef = useRef(new Map());
  const cached = (key, fn, ttl = 20000) => {
    const hit = cacheRef.current.get(key);
    if (hit && Date.now() - hit.at < ttl) return hit.promise;
    const promise = fn().catch((err) => {
      cacheRef.current.delete(key);
      throw err;
    });
    cacheRef.current.set(key, { promise, at: Date.now() });
    return promise;
  };
  const clearCache = () => cacheRef.current.clear();

  // Contract instances
  // Logic: all user actions + computed views
  const gainforceContract = useMemo(() => {
    const activeProvider = signer || provider || readOnlyProvider;
    return new ethers.Contract(ACTIVE_CONTRACTS.logic, LOGIC_ABI, activeProvider);
  }, [signer, provider, readOnlyProvider]);

  // Views: all computed reads (read-only provider is enough)
  const viewsContract = useMemo(() => {
    return new ethers.Contract(ACTIVE_CONTRACTS.views, VIEWS_ABI, readOnlyProvider);
  }, [readOnlyProvider]);

  // Storage: raw data reads (read-only provider is enough)
  const storageContract = useMemo(() => {
    return new ethers.Contract(ACTIVE_CONTRACTS.storage, STORAGE_ABI, readOnlyProvider);
  }, [readOnlyProvider]);

  // Token writes (approve) need the signer
  const tokenContract = useMemo(() => {
    const activeProvider = signer || provider || readOnlyProvider;
    return new ethers.Contract(ACTIVE_CONTRACTS.token, ERC20_ABI, activeProvider);
  }, [signer, provider, readOnlyProvider]);

  // Token reads go through the fast shared provider (wallet RPCs can be slow, esp. Trust Wallet)
  const tokenReadContract = useMemo(() => {
    return new ethers.Contract(ACTIVE_CONTRACTS.token, ERC20_ABI, readOnlyProvider);
  }, [readOnlyProvider]);

  // Check if user is registered (with retry for Trust Wallet)
  const checkRegistration = async (retryCount = 0) => {
    if (!account || !isCorrectChain) {
      setUserId(null);
      setIsRegistered(false);
      setIsOwner(false);
      setIsAdmin(false);
      setIsCheckingRegistration(false);
      return;
    }

    // Set checking state only on first attempt
    if (retryCount === 0) {
      setIsCheckingRegistration(true);
    }

    try {
      // storageContract uses readOnlyProvider to avoid signer issues
      const [id, ownerAddr, adminAddr] = await Promise.all([
        storageContract.addressToId(account),
        storageContract.owner(),
        storageContract.admin()
      ]);
      const idNumber = id.toNumber();

      const me = account.toLowerCase();
      const ownerMatch = ownerAddr.toLowerCase() === me;
      setIsOwner(ownerMatch);
      setIsAdmin(ownerMatch || adminAddr.toLowerCase() === me);

      if (idNumber > 0) {
        setUserId(idNumber);
        setIsRegistered(true);
      } else {
        setUserId(null);
        setIsRegistered(false);
      }
      setIsCheckingRegistration(false);
    } catch (err) {
      console.error('Error checking registration:', err);
      
      // Retry logic for Trust Wallet - retry up to 3 times with increasing delay
      if (retryCount < 3) {
        const delay = (retryCount + 1) * 500; // 500ms, 1000ms, 1500ms
        console.log(`Retrying registration check in ${delay}ms (attempt ${retryCount + 2}/4)`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return checkRegistration(retryCount + 1);
      }
      
      setUserId(null);
      setIsRegistered(false);
      setIsOwner(false);
      setIsAdmin(false);
      setIsCheckingRegistration(false);
    }
  };

  // Fetch token balance
  const fetchTokenBalance = async () => {
    if (!account || !isCorrectChain) {
      setTokenBalance('0');
      return;
    }

    try {
      const balance = await tokenReadContract.balanceOf(account);
      setTokenBalance(ethers.utils.formatEther(balance));
    } catch (err) {
      console.error('Error fetching token balance:', err);
    }
  };

  // Fetch token allowance
  const fetchTokenAllowance = async () => {
    if (!account || !isCorrectChain) {
      setTokenAllowance('0');
      return;
    }

    try {
      // Tokens are pulled by the Storage contract, so allowance is checked against it
      const allowance = await tokenReadContract.allowance(account, ACTIVE_CONTRACTS.storage);
      setTokenAllowance(ethers.utils.formatEther(allowance));
    } catch (err) {
      console.error('Error fetching token allowance:', err);
    }
  };

  // Approve tokens
  const approveTokens = async (amount) => {
    if (!signer) {
      showError('Please connect your wallet');
      return false;
    }

    try {
      setIsLoading(true);
      const amountWei = ethers.utils.parseEther(amount.toString());
      const tx = await tokenContract.approve(ACTIVE_CONTRACTS.storage, amountWei);
      await tx.wait();
      success('Tokens approved successfully!');
      await fetchTokenAllowance();
      return true;
    } catch (err) {
      console.error('Approve error:', err);
      showError(parseContractError(err, 'Failed to approve tokens'));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Register and invest
  const registerAndInvest = async (sponsorId, position, amount) => {
    if (!signer) {
      showError('Please connect your wallet');
      return false;
    }

    try {
      setIsLoading(true);
      const sponsor = sponsorId || DEFAULT_REFERRER_ID;
      const amountWei = ethers.utils.parseEther(amount.toString());
      
      const tx = await gainforceContract.registerAndInvest(sponsor, position, amountWei);
      await tx.wait();
      clearCache();
      
      success('Registration successful! Welcome to Gainforce!');
      await checkRegistration();
      await fetchTokenBalance();
      return true;
    } catch (err) {
      console.error('Register error:', err);
      showError(parseContractError(err, 'Registration failed'));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Register with placement
  const registerWithPlacement = async (sponsorId, placementId, position, amount) => {
    if (!signer) {
      showError('Please connect your wallet');
      return false;
    }

    try {
      setIsLoading(true);
      const sponsor = sponsorId || DEFAULT_REFERRER_ID;
      const amountWei = ethers.utils.parseEther(amount.toString());
      
      const tx = await gainforceContract.registerWithPlacement(sponsor, placementId, position, amountWei);
      await tx.wait();
      clearCache();
      
      success('Registration successful! Welcome to Gainforce!');
      await checkRegistration();
      await fetchTokenBalance();
      return true;
    } catch (err) {
      console.error('Register with placement error:', err);
      showError(parseContractError(err, 'Registration failed'));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Invest (existing user)
  const invest = async (amount) => {
    if (!signer) {
      showError('Please connect your wallet');
      return false;
    }

    try {
      setIsLoading(true);
      const amountWei = ethers.utils.parseEther(amount.toString());
      
      const tx = await gainforceContract.invest(amountWei);
      await tx.wait();
      clearCache();
      
      success('Investment successful!');
      await fetchTokenBalance();
      return true;
    } catch (err) {
      console.error('Invest error:', err);
      showError(parseContractError(err, 'Investment failed'));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Withdraw
  const withdraw = async (amount) => {
    if (!signer) {
      showError('Please connect your wallet');
      return false;
    }

    try {
      setIsLoading(true);
      const amountWei = ethers.utils.parseEther(amount.toString());
      
      const tx = await gainforceContract.withdraw(amountWei);
      await tx.wait();
      clearCache();
      
      success('Withdrawal successful!');
      await fetchTokenBalance();
      return true;
    } catch (err) {
      console.error('Withdraw error:', err);
      showError(parseContractError(err, 'Withdrawal failed'));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Get user info
  const getUserInfo = async (uid) => {
    try {
      const id = uid || userId;
      if (!id) return null;
      
      const info = await cached(`viewsContract.getUserInfo:${id}`, () => viewsContract.getUserInfo(id));
      return {
        wallet: info.wallet,
        sponsorId: info.sponsorId.toNumber(),
        referrerId: info.referrerId.toNumber(),
        totalInvestment: ethers.utils.formatEther(info.totalInvestment),
        currentPackage: ethers.utils.formatEther(info.currentPackage),
        firstPackage: ethers.utils.formatEther(info.firstPackage),
        active: info.active
      };
    } catch (err) {
      console.error('Error getting user info:', err);
      return null;
    }
  };

  // Get binary info
  const getBinaryInfo = async (uid) => {
    try {
      const id = uid || userId;
      if (!id) return null;
      
      const info = await cached(`viewsContract.getBinaryInfo:${id}`, () => viewsContract.getBinaryInfo(id));
      return {
        leftChild: info.leftChild.toNumber(),
        rightChild: info.rightChild.toNumber(),
        leftBusiness: ethers.utils.formatEther(info.leftBusiness),
        rightBusiness: ethers.utils.formatEther(info.rightBusiness),
        leftBusinessTotal: ethers.utils.formatEther(info.leftBusinessTotal),
        rightBusinessTotal: ethers.utils.formatEther(info.rightBusinessTotal),
        leftDirects: info.leftDirects.toNumber(),
        rightDirects: info.rightDirects.toNumber(),
        hasBinaryElig: info.hasBinaryElig
      };
    } catch (err) {
      console.error('Error getting binary info:', err);
      return null;
    }
  };

  // Get income info
  const getIncomeInfo = async (uid) => {
    try {
      const id = uid || userId;
      if (!id) return null;
      
      const info = await cached(`viewsContract.getIncomeInfo:${id}`, () => viewsContract.getIncomeInfo(id));
      return {
        totalROI: ethers.utils.formatEther(info.totalROI),
        totalBinary: ethers.utils.formatEther(info.totalBinary),
        totalReferral: ethers.utils.formatEther(info.totalReferral),
        totalIncome: ethers.utils.formatEther(info.totalIncome),
        totalWithdrawn: ethers.utils.formatEther(info.totalWithdrawn),
        roiWithdrawn: ethers.utils.formatEther(info.roiWithdrawn),
        binaryWithdrawn: ethers.utils.formatEther(info.binaryWithdrawn),
        referralWithdrawn: ethers.utils.formatEther(info.referralWithdrawn)
      };
    } catch (err) {
      console.error('Error getting income info:', err);
      return null;
    }
  };

  // Get available breakdown
  const getAvailableBreakdown = async (uid) => {
    try {
      const id = uid || userId;
      if (!id) return null;
      
      const info = await cached(`viewsContract.getAvailableBreakdown:${id}`, () => viewsContract.getAvailableBreakdown(id));
      return {
        availableROI: ethers.utils.formatEther(info.availableROI),
        availableBinary: ethers.utils.formatEther(info.availableBinary),
        availableReferral: ethers.utils.formatEther(info.availableReferral),
        totalAvailable: ethers.utils.formatEther(info.totalAvailable)
      };
    } catch (err) {
      console.error('Error getting available breakdown:', err);
      return null;
    }
  };

  // Get direct info
  const getDirectInfo = async (uid) => {
    try {
      const id = uid || userId;
      if (!id) return null;
      
      const info = await cached(`storageContract.getUser:${id}`, () => storageContract.getUser(id));
      return {
        directReferrals: info.directReferrals.toNumber(),
        teamCount: info.teamCount.toNumber(),
        directBusiness: ethers.utils.formatEther(info.directBusiness)
      };
    } catch (err) {
      console.error('Error getting direct info:', err);
      return null;
    }
  };

  // Get daily binary status
  const getDailyBinaryStatus = async (uid) => {
    try {
      const id = uid || userId;
      if (!id) return null;
      
      const info = await cached(`viewsContract.getDailyBinaryStatus:${id}`, () => viewsContract.getDailyBinaryStatus(id));
      return {
        dailyCap: ethers.utils.formatEther(info.dailyCap),
        matchedToday: ethers.utils.formatEther(info.matchedToday),
        remainingToday: ethers.utils.formatEther(info.remainingToday),
        nextResetTime: info.nextResetTime.toNumber()
      };
    } catch (err) {
      console.error('Error getting daily binary status:', err);
      return null;
    }
  };

  // Get investment
  const getInvestment = async (uid, index) => {
    try {
      const id = uid || userId;
      if (!id) return null;
      
      const info = await cached(`viewsContract.getInvestmentDetails:${id},${index}`, () => viewsContract.getInvestmentDetails(id, index));
      return {
        amount: ethers.utils.formatEther(info.amount),
        timestamp: info.timestamp.toNumber(),
        roiEarned: ethers.utils.formatEther(info.roiEarned),
        totalEarned: ethers.utils.formatEther(info.totalEarned),
        cap300: ethers.utils.formatEther(info.cap300),
        remaining: ethers.utils.formatEther(info.remaining),
        active: info.active,
        invType: info.invType,
        lastROITimestamp: info.lastROITimestamp.toNumber()
      };
    } catch (err) {
      console.error('Error getting investment:', err);
      return null;
    }
  };

  // Get investment count
  const getInvestmentCount = async (uid) => {
    try {
      const id = uid || userId;
      if (!id) return 0;
      
      const count = await cached(`storageContract.getInvestmentCount:${id}`, () => storageContract.getInvestmentCount(id));
      return count.toNumber();
    } catch (err) {
      console.error('Error getting investment count:', err);
      return 0;
    }
  };

  // Get all investments
  const getAllInvestments = async (uid) => {
    try {
      const id = uid || userId;
      if (!id) return [];
      
      const count = await getInvestmentCount(id);

      // Fetch all investments in parallel
      const list = await Promise.all(
        Array.from({ length: count }, (_, i) => getInvestment(id, i))
      );

      return list
        .map((inv, i) => (inv ? { ...inv, index: i } : null))
        .filter(Boolean);
    } catch (err) {
      console.error('Error getting all investments:', err);
      return [];
    }
  };

  // Get binary tree
  const getBinaryTree = async (uid) => {
    try {
      const id = uid || userId;
      if (!id) return null;
      
      // No getBinaryTree on-chain anymore - build 2 levels from Storage.getUser
      const root = await cached(`storageContract.getUser:${id}`, () => storageContract.getUser(id));
      const l = root.leftChild.toNumber();
      const r = root.rightChild.toNumber();
      const [lNode, rNode] = await Promise.all([
        l ? storageContract.getUser(l) : null,
        r ? storageContract.getUser(r) : null
      ]);
      return {
        left: l,
        right: r,
        leftLeft: lNode ? lNode.leftChild.toNumber() : 0,
        leftRight: lNode ? lNode.rightChild.toNumber() : 0,
        rightLeft: rNode ? rNode.leftChild.toNumber() : 0,
        rightRight: rNode ? rNode.rightChild.toNumber() : 0
      };
    } catch (err) {
      console.error('Error getting binary tree:', err);
      return null;
    }
  };

  // Get recent activities
  const getRecentActivities = async (uid, count = 20) => {
    try {
      const id = uid || userId;
      if (!id) return [];
      
      // Views returns oldest -> newest
      const res = await cached(`viewsContract.getRecentActivities:${id},${count}`, () => viewsContract.getRecentActivities(id, count));
      const result = [];

      for (let i = 0; i < res.amounts.length; i++) {
        const type = res.types[i];
        const ref = res.fromIds[i];
        result.push({
          amount: ethers.utils.formatEther(res.amounts[i]),
          timestamp: res.timestamps[i].toNumber(),
          type,
          // fromId means different things per type:
          // 1 Binary   -> matched volume (wei)  2 Referral -> user ID
          // 3 Withdraw -> 0                     4 ROI      -> investment index
          fromId: type === 2 ? ref.toNumber() : 0,
          matched: type === 1 ? ethers.utils.formatEther(ref) : null,
          investmentIndex: type === 4 ? ref.toNumber() : null
        });
      }

      // Newest first
      return result.reverse();
    } catch (err) {
      console.error('Error getting activities:', err);
      return [];
    }
  };

  // Get ROI rate
  const getROIRate = async (uid) => {
    try {
      const id = uid || userId;
      if (!id) return 100;
      
      const rate = await cached(`viewsContract.getROIRate:${id}`, () => viewsContract.getROIRate(id));
      return rate.toNumber();
    } catch (err) {
      console.error('Error getting ROI rate:', err);
      return 100;
    }
  };

  // Get time unit
  const getTimeUnit = async () => {
    try {
      const unit = await cached(`storageContract.TIME_UNIT:`, () => storageContract.TIME_UNIT(), 300000);
      return unit.toNumber();
    } catch (err) {
      console.error('Error getting time unit:', err);
      return 300; // default 5 minutes
    }
  };

  // Get withdraw config (min amount + deduction in bps)
  const getWithdrawConfig = async () => {
    try {
      const [min, deduction] = await Promise.all([
        storageContract.minWithdraw(),
        storageContract.withdrawDeduction()
      ]);
      return {
        minWithdraw: ethers.utils.formatEther(min),
        deductionBps: deduction.toNumber()
      };
    } catch (err) {
      console.error('Error getting withdraw config:', err);
      return { minWithdraw: '0', deductionBps: 1000 };
    }
  };

  // Get total users
  const getTotalUsers = async () => {
    try {
      const total = await cached(`storageContract.totalUsers:`, () => storageContract.totalUsers());
      return total.toNumber();
    } catch (err) {
      console.error('Error getting total users:', err);
      return 0;
    }
  };

  // Find placement
  const findPlacement = async (sponsorId, position, maxDepth = 100) => {
    try {
      // Mirrors Logic._findPlacement: follow the outer leg until an empty slot
      let curr = sponsorId || DEFAULT_REFERRER_ID;
      for (let depth = 0; depth < maxDepth; depth++) {
        const nodeId = curr;
        const u = await cached(`storageContract.getUser:${nodeId}`, () => storageContract.getUser(nodeId));
        const next = (position === 0 ? u.leftChild : u.rightChild).toNumber();
        if (next === 0) return { placementId: curr, depth };
        curr = next;
      }
      return null;
    } catch (err) {
      console.error('Error finding placement:', err);
      return null;
    }
  };

  // Effects
  useEffect(() => {
    // New wallet = fresh data
    clearCache();

    if (isConnected && isCorrectChain) {
      // All reads use the shared RPC provider (not the wallet), so no Trust Wallet
      // warm-up delay is needed - run everything in parallel
      checkRegistration();
      fetchTokenBalance();
      fetchTokenAllowance();
    } else {
      setUserId(null);
      setIsRegistered(false);
      setIsOwner(false);
      setIsAdmin(false);
      setTokenBalance('0');
      setTokenAllowance('0');
    }
  }, [account, isConnected, isCorrectChain]);

  const value = {
    gainforceContract,
    viewsContract,
    storageContract,
    tokenContract,
    userId,
    isRegistered,
    isOwner,
    isAdmin,
    isCheckingRegistration,
    isLoading,
    tokenBalance,
    tokenAllowance,
    approveTokens,
    registerAndInvest,
    registerWithPlacement,
    invest,
    withdraw,
    getUserInfo,
    getBinaryInfo,
    getIncomeInfo,
    getAvailableBreakdown,
    getDirectInfo,
    getDailyBinaryStatus,
    getInvestment,
    getInvestmentCount,
    getAllInvestments,
    getBinaryTree,
    getRecentActivities,
    getROIRate,
    getTimeUnit,
    getTotalUsers,
    getWithdrawConfig,
    findPlacement,
    checkRegistration,
    fetchTokenBalance,
    fetchTokenAllowance
  };

  return (
    <ContractContext.Provider value={value}>
      {children}
    </ContractContext.Provider>
  );
};

export default ContractContext;
